const KTP_RATIO = 85.6 / 53.98

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Unable to read image'))
    }
    image.src = url
  })
}

function createCanvas(width, height) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

function getCenterCrop(width, height, targetRatio) {
  const sourceRatio = width / height
  if (sourceRatio > targetRatio) {
    const cropHeight = height
    const cropWidth = Math.round(height * targetRatio)
    return {
      x: Math.round((width - cropWidth) / 2),
      y: 0,
      width: cropWidth,
      height: cropHeight,
      mode: 'guide-crop'
    }
  }

  const cropWidth = width
  const cropHeight = Math.round(width / targetRatio)
  return {
    x: 0,
    y: Math.round((height - cropHeight) / 2),
    width: cropWidth,
    height: cropHeight,
    mode: 'guide-crop'
  }
}

/**
 * Finds the strongest vertical and horizontal edges around the image centre.
 * This is intentionally a lightweight browser-only check. OCR/backend should
 * still validate the document type and fields.
 */
function detectCardBounds(image) {
  const maxSide = 480
  const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight))
  const width = Math.max(1, Math.round(image.naturalWidth * scale))
  const height = Math.max(1, Math.round(image.naturalHeight * scale))
  const canvas = createCanvas(width, height)
  const context = canvas.getContext('2d', { willReadFrequently: true })
  context.drawImage(image, 0, 0, width, height)
  const pixels = context.getImageData(0, 0, width, height).data
  const gray = new Uint8Array(width * height)

  for (let index = 0; index < gray.length; index += 1) {
    const pixel = index * 4
    gray[index] = Math.round(pixels[pixel] * 0.299 + pixels[pixel + 1] * 0.587 + pixels[pixel + 2] * 0.114)
  }

  const vertical = new Float32Array(width)
  const horizontal = new Float32Array(height)
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const current = gray[y * width + x]
      vertical[x] += Math.abs(current - gray[y * width + x - 1])
      horizontal[y] += Math.abs(current - gray[(y - 1) * width + x])
    }
  }

  const findPeak = (scores, start, end) => {
    let peak = start
    for (let index = start + 1; index < end; index += 1) {
      if (scores[index] > scores[peak]) peak = index
    }
    return peak
  }

  const left = findPeak(vertical, Math.round(width * 0.06), Math.round(width * 0.48))
  const right = findPeak(vertical, Math.round(width * 0.52), Math.round(width * 0.94))
  const top = findPeak(horizontal, Math.round(height * 0.06), Math.round(height * 0.48))
  const bottom = findPeak(horizontal, Math.round(height * 0.52), Math.round(height * 0.94))
  const cardWidth = right - left
  const cardHeight = bottom - top
  const ratio = cardWidth / cardHeight
  const areaRatio = (cardWidth * cardHeight) / (width * height)
  const score = Math.min(vertical[left], vertical[right]) + Math.min(horizontal[top], horizontal[bottom])
  const averageScore = (width + height) * 12

  if (cardWidth < width * 0.35 || cardHeight < height * 0.2 || ratio < 1.25 || ratio > 2.15 || areaRatio < 0.12 || score < averageScore) {
    return null
  }

  return {
    x: Math.round(left / scale),
    y: Math.round(top / scale),
    width: Math.round(cardWidth / scale),
    height: Math.round(cardHeight / scale),
    mode: 'edge-detected'
  }
}

function normalizeCrop(crop, width, height) {
  const x = Math.max(0, Math.min(crop.x, width - 1))
  const y = Math.max(0, Math.min(crop.y, height - 1))
  const cropWidth = Math.max(1, Math.min(crop.width, width - x))
  const cropHeight = Math.max(1, Math.min(crop.height, height - y))
  return { x, y, width: cropWidth, height: cropHeight, mode: crop.mode }
}

function fitToRatio(crop, targetRatio) {
  const currentRatio = crop.width / crop.height
  if (currentRatio > targetRatio) {
    const width = Math.round(crop.height * targetRatio)
    return { ...crop, x: crop.x + Math.round((crop.width - width) / 2), width }
  }
  const height = Math.round(crop.width / targetRatio)
  return { ...crop, y: crop.y + Math.round((crop.height - height) / 2), height }
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob)
      else reject(new Error('Unable to encode image'))
    }, 'image/jpeg', quality)
  })
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

/**
 * Checks whether the camera frame contains a large, continuous card-like
 * rectangle. This is a lightweight client-side gate, not an identity or
 * document authenticity check; OCR/backend validation is still required.
 */
export function inspectKtpCanvas(canvas) {
  const width = canvas.width
  const height = canvas.height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  const pixels = context.getImageData(0, 0, width, height).data
  const gray = new Uint8Array(width * height)
  const gradientX = new Float32Array(width * height)
  const gradientY = new Float32Array(width * height)
  let brightness = 0
  let brightnessSquared = 0
  let gradientTotal = 0
  let edgeCount = 0
  const pixelCount = width * height

  for (let index = 0; index < gray.length; index += 1) {
    const pixel = index * 4
    const value = pixels[pixel] * 0.299 + pixels[pixel + 1] * 0.587 + pixels[pixel + 2] * 0.114
    gray[index] = value
    brightness += value
    brightnessSquared += value * value
  }

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const index = y * width + x
      const gx = Math.abs(gray[index] - gray[index - 1])
      const gy = Math.abs(gray[index] - gray[index - width])
      gradientX[index] = gx
      gradientY[index] = gy
      gradientTotal += (gx + gy) / 2
      if (gx + gy > 58) edgeCount += 1
    }
  }

  brightness /= pixelCount
  const variance = brightnessSquared / pixelCount - brightness * brightness
  const averageGradient = gradientTotal / Math.max(1, (width - 2) * (height - 2))
  const edgeDensity = edgeCount / pixelCount
  // KTP 边框可能是浅色、带阴影或被反光削弱，阈值不能过高。
  const lineThreshold = Math.max(12, averageGradient * 1.35 + 5)

  const scoreVerticalLine = x => {
    const segmentSize = height * 0.82 / 8
    let total = 0
    let continuousSegments = 0
    for (let segment = 0; segment < 8; segment += 1) {
      const start = Math.round(height * 0.09 + segment * segmentSize)
      const end = Math.min(height - 1, Math.round(start + segmentSize))
      let score = 0
      let count = 0
      for (let y = start; y < end; y += 1) {
        for (let offset = -2; offset <= 2; offset += 1) {
          const sampleX = clamp(x + offset, 1, width - 2)
          score += gradientX[y * width + sampleX]
          count += 1
        }
      }
      const segmentScore = score / Math.max(1, count)
      total += segmentScore
      if (segmentScore >= lineThreshold) continuousSegments += 1
    }
    return {
      average: total / 8,
      continuity: continuousSegments / 8
    }
  }

  const scoreHorizontalLine = y => {
    const segmentSize = width * 0.82 / 8
    let total = 0
    let continuousSegments = 0
    for (let segment = 0; segment < 8; segment += 1) {
      const start = Math.round(width * 0.09 + segment * segmentSize)
      const end = Math.min(width - 1, Math.round(start + segmentSize))
      let score = 0
      let count = 0
      for (let x = start; x < end; x += 1) {
        for (let offset = -2; offset <= 2; offset += 1) {
          const sampleY = clamp(y + offset, 1, height - 2)
          score += gradientY[sampleY * width + x]
          count += 1
        }
      }
      const segmentScore = score / Math.max(1, count)
      total += segmentScore
      if (segmentScore >= lineThreshold) continuousSegments += 1
    }
    return {
      average: total / 8,
      continuity: continuousSegments / 8
    }
  }

  const findBestLine = (start, end, scorer) => {
    let best = { average: 0, continuity: 0, position: 0 }
    for (let position = Math.round(start); position <= Math.round(end); position += 2) {
      const candidate = scorer(position)
      const candidateScore = candidate.average * (0.55 + candidate.continuity)
      const bestScore = best.average * (0.55 + best.continuity)
      if (candidateScore > bestScore) best = { ...candidate, position }
    }
    return best
  }

  const left = findBestLine(width * 0.03, width * 0.35, scoreVerticalLine)
  const right = findBestLine(width * 0.65, width * 0.97, scoreVerticalLine)
  const top = findBestLine(height * 0.03, height * 0.35, scoreHorizontalLine)
  const bottom = findBestLine(height * 0.65, height * 0.97, scoreHorizontalLine)
  const lines = [left, right, top, bottom]
  const lineConfidence = Math.min(...lines.map(line => clamp(line.average / lineThreshold, 0, 1)))
  const continuity = Math.min(...lines.map(line => line.continuity))
  const cardWidthRatio = (right.position - left.position) / width
  const cardHeightRatio = (bottom.position - top.position) / height
  const cardRatio = (right.position - left.position) / Math.max(1, bottom.position - top.position)
  const geometryPassed = cardWidthRatio >= 0.48 && cardHeightRatio >= 0.38 && cardRatio >= 1.1 && cardRatio <= 2.15
  const cardDetected = lineConfidence >= 0.55 && continuity >= 0.25 && geometryPassed
  const passed = cardDetected && brightness > 28 && brightness < 240 && variance > 80 && edgeDensity > 0.012

  return {
    passed,
    cardDetected,
    brightness: Math.round(brightness),
    edgeDensity: Number(edgeDensity.toFixed(3)),
    lineConfidence: Number(lineConfidence.toFixed(2)),
    continuity: Number(continuity.toFixed(2)),
    cardRatio: Number(cardRatio.toFixed(2)),
    reason: !cardDetected ? 'no-card' : brightness <= 28 ? 'too-dark' : brightness >= 240 ? 'too-bright' : variance <= 80 || edgeDensity <= 0.012 ? 'low-quality' : ''
  }
}

function makeFile(blob, fileName) {
  try {
    return new File([blob], fileName, { type: 'image/jpeg', lastModified: Date.now() })
  } catch (error) {
    blob.name = fileName
    return blob
  }
}

function inspectCanvas(canvas) {
  const sampleCanvas = createCanvas(160, Math.max(1, Math.round(160 / KTP_RATIO)))
  const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true })
  sampleContext.drawImage(canvas, 0, 0, sampleCanvas.width, sampleCanvas.height)
  const pixels = sampleContext.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height).data
  let brightness = 0
  let brightnessSquared = 0
  let edgeCount = 0
  const pixelCount = sampleCanvas.width * sampleCanvas.height

  for (let y = 1; y < sampleCanvas.height - 1; y += 1) {
    for (let x = 1; x < sampleCanvas.width - 1; x += 1) {
      const index = (y * sampleCanvas.width + x) * 4
      const current = pixels[index] * 0.299 + pixels[index + 1] * 0.587 + pixels[index + 2] * 0.114
      const previousX = (y * sampleCanvas.width + x - 1) * 4
      const previousY = ((y - 1) * sampleCanvas.width + x) * 4
      const xGray = pixels[previousX] * 0.299 + pixels[previousX + 1] * 0.587 + pixels[previousX + 2] * 0.114
      const yGray = pixels[previousY] * 0.299 + pixels[previousY + 1] * 0.587 + pixels[previousY + 2] * 0.114
      const value = Math.max(0, Math.min(255, current))
      brightness += value
      brightnessSquared += value * value
      if (Math.abs(value - xGray) + Math.abs(value - yGray) > 55) edgeCount += 1
    }
  }

  brightness /= pixelCount
  const variance = brightnessSquared / pixelCount - brightness * brightness
  const edgeDensity = edgeCount / pixelCount
  const passed = brightness > 35 && brightness < 235 && variance > 80 && edgeDensity > 0.015
  return {
    passed,
    brightness: Math.round(brightness),
    edgeDensity: Number(edgeDensity.toFixed(3)),
    reason: brightness <= 35 ? 'too-dark' : brightness >= 235 ? 'too-bright' : variance <= 80 || edgeDensity <= 0.015 ? 'low-quality' : ''
  }
}

export async function processKtpImage(file, options = {}) {
  const image = await loadImage(file)
  const detectedCrop = detectCardBounds(image)
  const fallbackCrop = getCenterCrop(image.naturalWidth, image.naturalHeight, KTP_RATIO)
  const crop = fitToRatio(normalizeCrop(detectedCrop || fallbackCrop, image.naturalWidth, image.naturalHeight), KTP_RATIO)
  const maxWidth = options.maxWidth || 1600
  const outputWidth = Math.min(maxWidth, crop.width)
  const outputHeight = Math.round(outputWidth / KTP_RATIO)
  const canvas = createCanvas(outputWidth, outputHeight)
  const context = canvas.getContext('2d')
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, outputWidth, outputHeight)

  let blob
  for (const quality of [options.quality || 0.84, 0.74, 0.64, 0.54]) {
    blob = await canvasToBlob(canvas, quality)
    if (blob.size <= (options.maxBytes || 1.8 * 1024 * 1024) || quality === 0.54) break
  }

  const fileName = `ktp-${options.side || 'front'}-${Date.now()}.jpg`
  const outputFile = makeFile(blob, fileName)
  return {
    file: outputFile,
    blob,
    base64: await blobToBase64(blob),
    width: outputWidth,
    height: outputHeight,
    size: blob.size,
    cropMode: crop.mode,
    quality: inspectCanvas(canvas)
  }
}

export { KTP_RATIO }
